import React, { useState } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';

const ComplianceCostEstimator = ({ onEstimate }) => {
  const [companySize, setCompanySize] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [dataHandling, setDataHandling] = useState([]);
  const [regulatoryRequirements, setRegulatoryRequirements] = useState([]);
  const [showResults, setShowResults] = useState(false);

  const companySizes = [
    { value: 'startup', label: 'Startup (1-50 employees)' },
    { value: 'sme', label: 'SME (51-500 employees)' },
    { value: 'enterprise', label: 'Enterprise (501-5000 employees)' },
    { value: 'large-enterprise', label: 'Large Enterprise (5000+ employees)' }
  ];

  const businessTypes = [
    { value: 'fintech', label: 'Financial Services/Fintech' },
    { value: 'healthcare', label: 'Healthcare/Life Sciences' },
    { value: 'ecommerce', label: 'E-commerce/Retail' },
    { value: 'saas', label: 'SaaS/Technology' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'consulting', label: 'Consulting/Professional Services' },
    { value: 'media', label: 'Media/Entertainment' },
    { value: 'education', label: 'Education/EdTech' }
  ];

  const dataHandlingOptions = [
    { value: 'personal-data', label: 'Personal Data Processing', cost: 25000 },
    { value: 'financial-data', label: 'Financial Data Handling', cost: 45000 },
    { value: 'health-data', label: 'Health/Medical Data', cost: 55000 },
    { value: 'payment-processing', label: 'Payment Processing', cost: 35000 },
    { value: 'cross-border', label: 'Cross-border Data Transfer', cost: 30000 },
    { value: 'data-localization', label: 'Data Localization Requirements', cost: 40000 }
  ];

  const regulatoryOptions = [
    { value: 'gdpr', label: 'GDPR Compliance', cost: 50000 },
    { value: 'sox', label: 'SOX Compliance', cost: 75000 },
    { value: 'hipaa', label: 'HIPAA Compliance', cost: 60000 },
    { value: 'pci-dss', label: 'PCI DSS Compliance', cost: 40000 },
    { value: 'iso27001', label: 'ISO 27001 Certification', cost: 35000 },
    { value: 'rbi-guidelines', label: 'RBI Guidelines', cost: 45000 },
    { value: 'sebi-compliance', label: 'SEBI Compliance', cost: 55000 },
    { value: 'dpdp-act', label: 'DPDP Act 2023', cost: 30000 }
  ];

  const handleDataHandlingChange = (value, checked) => {
    if (checked) {
      setDataHandling(prev => [...prev, value]);
    } else {
      setDataHandling(prev => prev.filter(item => item !== value));
    }
  };

  const handleRegulatoryChange = (value, checked) => {
    if (checked) {
      setRegulatoryRequirements(prev => [...prev, value]);
    } else {
      setRegulatoryRequirements(prev => prev.filter(item => item !== value));
    }
  };

  const calculateCosts = () => {
    const baseCosts = {
      'startup': 15000,
      'sme': 25000,
      'enterprise': 50000,
      'large-enterprise': 100000
    };

    const businessMultipliers = {
      'fintech': 1.5,
      'healthcare': 1.4,
      'ecommerce': 1.2,
      'saas': 1.1,
      'manufacturing': 1.0,
      'consulting': 0.9,
      'media': 1.1,
      'education': 0.8
    };

    const baseCost = baseCosts[companySize] || 0;
    const businessMultiplier = businessMultipliers[businessType] || 1;
    
    const dataHandlingCosts = dataHandling.reduce((total, item) => {
      const option = dataHandlingOptions.find(opt => opt.value === item);
      return total + (option?.cost || 0);
    }, 0);

    const regulatoryCosts = regulatoryRequirements.reduce((total, item) => {
      const option = regulatoryOptions.find(opt => opt.value === item);
      return total + (option?.cost || 0);
    }, 0);

    const totalSetupCost = (baseCost + dataHandlingCosts + regulatoryCosts) * businessMultiplier;
    const annualMaintenanceCost = totalSetupCost * 0.3;
    const auditCosts = totalSetupCost * 0.15;

    return {
      setupCost: Math.round(totalSetupCost),
      annualMaintenance: Math.round(annualMaintenanceCost),
      auditCosts: Math.round(auditCosts),
      totalFirstYear: Math.round(totalSetupCost + annualMaintenanceCost + auditCosts),
      breakdown: {
        base: Math.round(baseCost * businessMultiplier),
        dataHandling: Math.round(dataHandlingCosts * businessMultiplier),
        regulatory: Math.round(regulatoryCosts * businessMultiplier)
      }
    };
  };

  const handleEstimate = () => {
    if (companySize && businessType) {
      setShowResults(true);
      onEstimate({
        companySize,
        businessType,
        dataHandling,
        regulatoryRequirements,
        costs: calculateCosts()
      });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const costs = showResults ? calculateCosts() : null;

  const pieData = costs ? [
    { name: 'Base Compliance', value: costs.breakdown.base, color: '#1a2332' },
    { name: 'Data Handling', value: costs.breakdown.dataHandling, color: '#2c3e50' },
    { name: 'Regulatory', value: costs.breakdown.regulatory, color: '#d4af37' }
  ] : [];

  const yearlyProjection = costs ? [
    { year: 'Year 1', setup: costs.setupCost, maintenance: costs.annualMaintenance, audit: costs.auditCosts },
    { year: 'Year 2', setup: 0, maintenance: costs.annualMaintenance, audit: costs.auditCosts },
    { year: 'Year 3', setup: 0, maintenance: costs.annualMaintenance, audit: costs.auditCosts },
    { year: 'Year 4', setup: 0, maintenance: costs.annualMaintenance, audit: costs.auditCosts },
    { year: 'Year 5', setup: 0, maintenance: costs.annualMaintenance, audit: costs.auditCosts }
  ] : [];

  return (
    <div 
      className="rounded-xl p-6 shadow-executive"
      style={{
        backgroundColor: '#0f172a',
        border: '1px solid #475569',
        color: '#f8fafc'
      }}
    >
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 gradient-executive rounded-lg flex items-center justify-center">
          <Icon name="Shield" size={20} color="white" />
        </div>
        <div>
          <h3 
            className="font-headline font-semibold text-lg"
            style={{ color: '#f8fafc' }}
          >
            Compliance Cost Estimator
          </h3>
          <p 
            className="text-sm"
            style={{ color: '#cbd5e1' }}
          >
            Estimate regulatory compliance costs for your GCC
          </p>
        </div>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Company Size"
            placeholder="Select company size"
            options={companySizes}
            value={companySize}
            onChange={setCompanySize}
            description="Size affects compliance complexity"
            required
          />

          <Select
            label="Business Type"
            placeholder="Select business type"
            options={businessTypes}
            value={businessType}
            onChange={setBusinessType}
            description="Industry affects regulatory requirements"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 
              className="font-medium mb-3"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Data Handling Requirements
            </h4>
            <div className="space-y-3 max-h-48 overflow-y-auto">
              {dataHandlingOptions.map((option) => (
                <Checkbox
                  key={option.value}
                  label={`${option.label} (${formatCurrency(option.cost)})`}
                  checked={dataHandling.includes(option.value)}
                  onChange={(e) => handleDataHandlingChange(option.value, e.target.checked)}
                />
              ))}
            </div>
          </div>

          <div>
            <h4 
              className="font-medium mb-3"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Regulatory Requirements
            </h4>
            <div className="space-y-3 max-h-48 overflow-y-auto">
              {regulatoryOptions.map((option) => (
                <Checkbox
                  key={option.value}
                  label={`${option.label} (${formatCurrency(option.cost)})`}
                  checked={regulatoryRequirements.includes(option.value)}
                  onChange={(e) => handleRegulatoryChange(option.value, e.target.checked)}
                />
              ))}
            </div>
          </div>
        </div>

        <div 
          className="rounded-lg p-4"
          style={{ backgroundColor: 'var(--color-muted)' }}
        >
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={20} color="var(--color-accent)" />
            <div>
              <h4 
                className="font-medium mb-1"
                style={{ color: 'var(--color-text-primary)' }}
              >
                Cost Components
              </h4>
              <p 
                className="text-sm"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                Estimates include setup costs, annual maintenance, audit fees, and ongoing compliance monitoring. Costs vary based on implementation complexity and third-party service requirements.
              </p>
            </div>
          </div>
        </div>

        <Button
          variant="default"
          size="lg"
          fullWidth
          onClick={handleEstimate}
          disabled={!companySize || !businessType}
          className="bg-cta hover:bg-cta/90 text-cta-foreground"
          iconName="Calculator"
          iconPosition="left"
        >
          Calculate Compliance Costs
        </Button>

        {showResults && costs && (
          <div className="space-y-6 pt-6 border-t border-border">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-primary/5 rounded-lg">
                <div className="text-2xl font-bold text-text-primary mb-1">
                  {formatCurrency(costs.setupCost)}
                </div>
                <div className="text-sm text-text-secondary">Setup Cost</div>
              </div>
              
              <div className="text-center p-4 bg-accent/5 rounded-lg">
                <div className="text-2xl font-bold text-accent mb-1">
                  {formatCurrency(costs.annualMaintenance)}
                </div>
                <div className="text-sm text-text-secondary">Annual Maintenance</div>
              </div>
              
              <div className="text-center p-4 bg-secondary/5 rounded-lg">
                <div className="text-2xl font-bold text-secondary mb-1">
                  {formatCurrency(costs.auditCosts)}
                </div>
                <div className="text-sm text-text-secondary">Audit Costs</div>
              </div>
              
              <div className="text-center p-4 bg-success/5 rounded-lg">
                <div className="text-2xl font-bold text-success mb-1">
                  {formatCurrency(costs.totalFirstYear)}
                </div>
                <div className="text-sm text-text-secondary">Total First Year</div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-text-primary mb-4">Cost Breakdown</h4>
                <div className="h-64 flex items-center justify-center">
                  <PieChart width={400} height={256}>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </PieChart>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-text-primary mb-4">5-Year Projection</h4>
                <div className="h-64 flex items-center justify-center">
                  <BarChart width={Math.min(window.innerWidth - 100, 800)} height={256} data={yearlyProjection}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                      <XAxis dataKey="year" stroke="var(--color-text-secondary)" />
                      <YAxis stroke="var(--color-text-secondary)" tickFormatter={formatCurrency} />
                      <Tooltip 
                        formatter={(value) => formatCurrency(value)}
                        contentStyle={{
                          backgroundColor: 'var(--color-card)',
                          border: '1px solid var(--color-border)',
                          borderRadius: '8px'
                        }}
                      />
                      <Bar dataKey="setup" stackId="a" fill="var(--color-primary)" name="Setup" />
                      <Bar dataKey="maintenance" stackId="a" fill="var(--color-accent)" name="Maintenance" />
                      <Bar dataKey="audit" stackId="a" fill="var(--color-secondary)" name="Audit" />
                    </BarChart>
                </div>
              </div>
            </div>

            <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
                <div>
                  <h5 className="font-medium text-text-primary mb-1">Important Considerations</h5>
                  <ul className="text-sm text-text-secondary space-y-1">
                    <li>• Costs may vary based on specific implementation requirements</li>
                    <li>• Additional costs may apply for specialized compliance consulting</li>
                    <li>• Regular updates may be required as regulations evolve</li>
                    <li>• Consider ongoing training and certification costs for staff</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceCostEstimator;